/**
 * @fileoverview Shared builder-config model + section catalog for the document template
 * builders (tenant-web + system-web). Single source of truth so both apps and both edit
 * surfaces (Section list + Canvas selection panel) use one model and one set of defaults.
 * @module ui-admin/template-builder/documentSectionEditors/types
 *
 * Responsibilities: - Define the builder-side DocumentConfig / SectionConfig / SectionId types.
 *                   - Provide DEFAULT_SECTIONS, SECTION_ICONS, DEFAULT_CONFIG used by both pages.
 * Dependencies:     - lucide-react (section icons); @firesquirrel/document-render (ThemePresetId).
 * Architecture:     - Pure data/types, no I/O. Extracted from the two builder pages to close
 *                     G-DRY-01; previously duplicated verbatim in each page.
 * Notes:            - This is the *builder* DocumentConfig (rich 6-value documentType + per-section
 *                     configs). It is structurally compatible with @firesquirrel/local-db's
 *                     BuilderDocumentConfig used by reportTemplateTransform.
 */

import type React from 'react';
import {
  FileText, Layout, User, Type, MessageSquare, ClipboardList,
  AlertTriangle, Camera, StickyNote, Phone, PenTool, Scale, Paperclip, UsersRound,
} from 'lucide-react';
import type { ThemePresetId, ElementStyle, DocumentThemeCustom } from '@firesquirrel/document-render';

/**
 * @contract SectionId
 * Purpose: Enumerates the document sections a builder template can contain.
 * Fields:  union of section identifiers.
 * Rules:   must match SECTION_ICONS keys and DEFAULT_SECTIONS ids.
 * Sync:    none (UI model only; persisted via reportTemplateTransform).
 */
export type SectionId =
  | 'complexVisitSummary'
  | 'complexParticipants'
  | 'cover'
  | 'header'
  | 'recipientBlock'
  | 'subjectLine'
  | 'salutation'
  | 'bodyText'
  | 'metadata'
  | 'violations'
  | 'photos'
  | 'comments'
  | 'contactBlock'
  | 'signatures'
  | 'legal'
  | 'footer'
  | 'staticContent';

/**
 * @contract SectionConfig
 * Purpose: A single section's list-level metadata (label/icon/description/enabled).
 * Fields:  id, label, icon, description, enabled, pageBreakBefore?
 * Rules:   id is a SectionId; icon is a React component type (not serialized — see SECTION_ICONS).
 * Sync:    none (UI model only).
 */
export interface SectionConfig {
  id: SectionId;
  label: string;
  icon: React.ElementType;
  description: string;
  enabled: boolean;
  pageBreakBefore?: boolean;
}

/**
 * @contract DocumentConfig
 * Purpose: The full builder-side template model edited by the Section/Canvas surfaces.
 * Fields:  documentType, layoutMode, page options, sections[], and per-section configs.
 * Rules:   documentType is the rich 6-value union (mapped to the collapsed spec enum on save).
 * Sync:    local-first; persisted through reportTemplateTransform (builderConfigBag round-trip).
 */
/** @contract ComplexVisitSummaryConfig — immutable builder flags for complex summary rendering. */
export interface ComplexVisitSummaryConfig {
  showComplexName: boolean;
  showAnchor: boolean;
  showVisitDate: boolean;
  showParticipantCounts: boolean;
  showStandaloneSummary: boolean;
}

/** @contract ComplexParticipantsConfig — immutable builder flags for governed participants. */
export interface ComplexParticipantsConfig {
  includeRootEntity: boolean;
  includeGovernedMembers: boolean;
  includeStandaloneSummary: boolean;
  startEachEntityOnNewPage: boolean;
  showEntityHeader: boolean;
  showEntityAddress: boolean;
  showEntityStatus: boolean;
  showQuestionsResponses: boolean;
  showViolations: boolean;
  showCorrections: boolean;
  showPermits: boolean;
  showAssets: boolean;
  showPhotos: boolean;
  showSignatures: boolean;
  entitySort: 'visitOrder' | 'entityName' | 'entityCategory';
}

export interface DocumentConfig {
  documentType: 'inspectionReport' | 'permit' | 'noticeOfViolation' | 'letter' | 'certificateOfCompliance' | 'custom';
  layoutMode: 'form' | 'letter';
  pageSize: 'letter' | 'a4' | 'legal';
  orientation: 'portrait' | 'landscape';
  approvalMode: 'autoSend' | 'requireApproval';
  reportDataScope: 'singleCase' | 'complexVisit';
  sections: SectionConfig[];
  complexVisitSummary?: ComplexVisitSummaryConfig;
  complexParticipants?: ComplexParticipantsConfig;
  header?: {
    showLogo: boolean;
    logoPosition: 'left' | 'center' | 'right';
    showSecondLogo: boolean;
    secondLogoPosition: 'left' | 'right';
    showDepartmentName: boolean;
    showDepartmentAddress: boolean;
    showDepartmentPhone: boolean;
    showDepartmentFax: boolean;
    showDepartmentWebsite: boolean;
    customTitle?: string;
    customSubtitle?: string;
    titleStyle?: ElementStyle;
    subtitleStyle?: ElementStyle;
  };
  recipientBlock?: {
    style: 'grid' | 'letter-address';
    showOwnerName: boolean;
    showContactNumber: boolean;
    showApn: boolean;
    showInspectionAddress: boolean;
    showEmailAddress: boolean;
    showMailingAddress: boolean;
    showInspectionNumber: boolean;
  };
  subjectLine?: {
    prefix: string;
    template: string;
  };
  salutation?: {
    text: string;
  };
  bodyText?: {
    paragraphs: Array<{
      id: string;
      content: string;
      condition?: 'always' | 'hasViolations' | 'noViolations';
      isHtml?: boolean;
    }>;
  };
  metadata?: {
    showComplianceStatus: boolean;
    showInspectionType: boolean;
    showInspectionDate: boolean;
    showSlope: boolean;
    showInspector: boolean;
    showAcreageEligible: boolean;
    showPhotosFlag: boolean;
    showCurbsideVacant: boolean;
    showInsuranceSolicited: boolean;
    showComplaintConstruction: boolean;
  };
  violationConfig?: {
    displayFormat: 'checklist' | 'narrative' | 'table' | 'grouped';
    /**
     * Grouping mode. 'question' nests each violation (code + occurrences) under the inspection
     * question that produced it, with question-less violations collected into a trailing
     * "Additional Violations" section (G-VIOL-QGROUP). Other modes group by the named axis.
     */
    groupBy: 'zone' | 'category' | 'severity' | 'none' | 'question';
    showCodeReferences: boolean;
    showLocationDescriptions: boolean;
    conditionalDisplay: 'always' | 'whenPresent';
    /** Color items by pass/fail result (pass green, fail red). G-VIOL-COLOR. */
    resultColoring?: boolean;
    passColor?: string;
    failColor?: string;
    checkboxMarkStyle?: 'box' | 'check' | 'cross' | 'checkCross';
  };
  photoConfig?: {
    // G-505: matches the canonical PhotoConfig.layout union in @firesquirrel/document-render.
    // Legacy templates persisted the removed 'gallery' value; it is coerced to 'grid' on edit.
    layout: 'inline' | 'grid' | 'appendix';
    maxPhotos: number;
    showCaptions: boolean;
    thumbnailSize: 'small' | 'medium' | 'large';
  };
  comments?: {
    showCommentsBox: boolean;
    additionalFields: Array<{ id: string; label: string; type: 'text' | 'number' }>;
  };
  contactBlock?: {
    introText: string;
    showPhone: boolean;
    showEmail: boolean;
    showFax: boolean;
  };
  signatures?: {
    style: 'initials' | 'full' | 'both';
    inspectorSignature: boolean;
    responsiblePartySignature: boolean;
    witnessSignature: boolean;
    dateFields: boolean;
    printedNameFields: boolean;
    showTitle: boolean;
  };
  legalText?: {
    enabled: boolean;
    content: string;
    position: 'footer' | 'lastPage' | 'both';
  };
  footerConfig?: {
    showDepartmentInfo: boolean;
    showPageNumbers: boolean;
    customText?: string;
  };
  staticContent?: {
    type: 'uploadedFile' | 'html';
    uploadedFileId?: string;
    htmlContent?: string;
    label?: string;
  };
  coverPage?: {
    enabled: boolean;
    showLogo: boolean;
    showEntityInfo: boolean;
    showInspectionSummary: boolean;
    showResultBadge: boolean;
  };
  permitConfig?: {
    numberPrefix: string;
    showConditions: boolean;
    showPaymentSummary: boolean;
    showExpirationDate: boolean;
    showRenewalInfo: boolean;
  };
  /** Named color-theme preset applied to the rendered document (G-THEME). */
  themePreset?: ThemePresetId;
  /** Custom theme overrides layered on top of themePreset (Phase E2). */
  themeCustom?: DocumentThemeCustom;
}

/** Default section list (order + enabled defaults) shown in the Section navigator. */
export const DEFAULT_SECTIONS: SectionConfig[] = [
  { id: 'complexVisitSummary', label: 'Complex Visit Summary', icon: ClipboardList, description: 'Complex name, anchor, visit date, and participant counts', enabled: false },
  { id: 'complexParticipants', label: 'Complex Participants', icon: UsersRound, description: 'Per-entity report sections for governed participants', enabled: false },
  { id: 'cover', label: 'Cover Page', icon: FileText, description: 'Optional cover with logo and summary', enabled: false },
  { id: 'header', label: 'Header', icon: Layout, description: 'Department branding, logos, and report title', enabled: true },
  { id: 'recipientBlock', label: 'Recipient / Property', icon: User, description: 'Owner/tenant name, address, APN, contacts', enabled: true },
  { id: 'subjectLine', label: 'Subject Line', icon: Type, description: 'RE: line with inspection type reference', enabled: false },
  { id: 'salutation', label: 'Salutation', icon: MessageSquare, description: 'Dear Property Owner greeting', enabled: false },
  { id: 'bodyText', label: 'Body Text', icon: FileText, description: 'Narrative paragraphs with merge fields', enabled: false },
  { id: 'metadata', label: 'Inspection Metadata', icon: ClipboardList, description: 'Compliance status, type, slope, inspector', enabled: true },
  { id: 'violations', label: 'Violations', icon: AlertTriangle, description: 'Checklist, narrative, or table of violations', enabled: true },
  { id: 'photos', label: 'Photos', icon: Camera, description: 'Inspection photos', enabled: true },
  { id: 'comments', label: 'Comments', icon: StickyNote, description: 'Free-text comments and custom fields', enabled: true },
  { id: 'contactBlock', label: 'Contact Info', icon: Phone, description: 'For questions, contact the department', enabled: false },
  { id: 'signatures', label: 'Signatures', icon: PenTool, description: 'Inspector and responsible party signatures', enabled: true },
  { id: 'legal', label: 'Legal Text', icon: Scale, description: 'Disclaimers and legal language', enabled: true },
  { id: 'footer', label: 'Footer', icon: Layout, description: 'Page footer with dept info and page numbers', enabled: true },
  { id: 'staticContent', label: 'Static Attachment', icon: Paperclip, description: 'Attached static pages (educational guides, etc.)', enabled: false },
];

/** Map section IDs to their icons (icons can't be serialized to JSON). */
export const SECTION_ICONS: Record<SectionId, React.ElementType> = {
  complexVisitSummary: ClipboardList,
  complexParticipants: UsersRound,
  cover: FileText,
  header: Layout,
  recipientBlock: User,
  subjectLine: Type,
  salutation: MessageSquare,
  bodyText: FileText,
  metadata: ClipboardList,
  violations: AlertTriangle,
  photos: Camera,
  comments: StickyNote,
  contactBlock: Phone,
  signatures: PenTool,
  legal: Scale,
  footer: Layout,
  staticContent: Paperclip,
};

/** Default builder template config used when creating a new document template. */
export const DEFAULT_CONFIG: DocumentConfig = {
  documentType: 'inspectionReport',
  layoutMode: 'form',
  pageSize: 'letter',
  orientation: 'portrait',
  approvalMode: 'requireApproval',
  reportDataScope: 'singleCase',
  sections: DEFAULT_SECTIONS,
  complexVisitSummary: {
    showComplexName: true,
    showAnchor: true,
    showVisitDate: true,
    showParticipantCounts: true,
    showStandaloneSummary: true,
  },
  complexParticipants: {
    includeRootEntity: true,
    includeGovernedMembers: true,
    includeStandaloneSummary: true,
    startEachEntityOnNewPage: true,
    showEntityHeader: true,
    showEntityAddress: true,
    showEntityStatus: true,
    showQuestionsResponses: true,
    showViolations: true,
    showCorrections: true,
    showPermits: true,
    showAssets: true,
    showPhotos: true,
    showSignatures: true,
    entitySort: 'visitOrder',
  },
  header: {
    showLogo: true,
    logoPosition: 'left',
    showSecondLogo: false,
    secondLogoPosition: 'right',
    showDepartmentName: true,
    showDepartmentAddress: true,
    showDepartmentPhone: true,
    showDepartmentFax: false,
    showDepartmentWebsite: false,
  },
  recipientBlock: {
    style: 'grid',
    showOwnerName: true,
    showContactNumber: true,
    showApn: true,
    showInspectionAddress: true,
    showEmailAddress: true,
    showMailingAddress: true,
    showInspectionNumber: true,
  },
  subjectLine: {
    prefix: 'RE:',
    template: '{{inspectionType}} Inspection',
  },
  salutation: {
    text: 'Dear Property Owner(s),',
  },
  bodyText: {
    paragraphs: [
      { id: 'intro', content: 'On {{date}}, the {{departmentName}} conducted a {{inspectionType}} inspection of the above-referenced property in accordance with applicable codes and standards.', condition: 'always' },
      { id: 'violations-found', content: 'During the inspection, the following corrective actions were identified:', condition: 'hasViolations' },
      { id: 'compliance', content: 'The property listed below was found to be in compliance with these standards and is hereby granted this Letter of Compliance.', condition: 'noViolations' },
    ],
  },
  metadata: {
    showComplianceStatus: true,
    showInspectionType: true,
    showInspectionDate: false,
    showSlope: false,
    showInspector: true,
    showAcreageEligible: false,
    showPhotosFlag: false,
    showCurbsideVacant: false,
    showInsuranceSolicited: false,
    showComplaintConstruction: false,
  },
  violationConfig: {
    displayFormat: 'checklist',
    groupBy: 'zone',
    showCodeReferences: true,
    showLocationDescriptions: true,
    conditionalDisplay: 'always',
    resultColoring: false,
    passColor: '#16a34a',
    failColor: '#dc2626',
    checkboxMarkStyle: 'box',
  },
  photoConfig: {
    layout: 'inline',
    maxPhotos: 20,
    showCaptions: true,
    thumbnailSize: 'medium',
  },
  comments: {
    showCommentsBox: true,
    additionalFields: [],
  },
  contactBlock: {
    introText: 'For questions regarding this matter, contact the {{departmentName}} at',
    showPhone: true,
    showEmail: true,
    showFax: false,
  },
  signatures: {
    style: 'full',
    inspectorSignature: true,
    responsiblePartySignature: true,
    witnessSignature: false,
    dateFields: true,
    printedNameFields: true,
    showTitle: true,
  },
  legalText: {
    enabled: true,
    content: '',
    position: 'footer',
  },
  footerConfig: {
    showDepartmentInfo: true,
    showPageNumbers: true,
  },
  staticContent: {
    type: 'uploadedFile',
    label: 'Educational Guide',
  },
};

/**
 * @process applyReportDataScopeToDocumentConfig
 * Steps: Remove all complex rows; locate header; insert one summary and one
 *   participant row immediately after header or at the beginning; set enabled flags.
 * Inputs: Existing immutable DocumentConfig and target singleCase/complexVisit scope.
 * Outputs: New DocumentConfig with canonical complex-row order and scope.
 * Failure Handling: Missing header uses index zero; no input mutation or throw.
 * Idempotency: REQUIRED — repeated application produces one canonical pair.
 */
export function applyReportDataScopeToDocumentConfig(
  config: DocumentConfig,
  scope: 'singleCase' | 'complexVisit',
): DocumentConfig {
  const summary = config.sections.find((section) => section.id === 'complexVisitSummary') ?? {
    id: 'complexVisitSummary' as const,
    label: 'Complex Visit Summary',
    icon: ClipboardList,
    description: 'Complex name, anchor, visit date, and participant counts',
    enabled: false,
  };
  const participants = config.sections.find((section) => section.id === 'complexParticipants') ?? {
    id: 'complexParticipants' as const,
    label: 'Complex Participants',
    icon: UsersRound,
    description: 'Per-entity report sections for governed participants',
    enabled: false,
  };
  const otherSections = config.sections.filter(
    (section) => section.id !== 'complexVisitSummary' && section.id !== 'complexParticipants',
  );
  const headerIndex = otherSections.findIndex((section) => section.id === 'header');
  const insertAt = headerIndex < 0 ? 0 : headerIndex + 1;
  const complexSections: SectionConfig[] = [
    { ...summary, enabled: scope === 'complexVisit' },
    { ...participants, enabled: scope === 'complexVisit' },
  ];
  return {
    ...config,
    reportDataScope: scope,
    sections: [
      ...otherSections.slice(0, insertAt),
      ...complexSections,
      ...otherSections.slice(insertAt),
    ],
  };
}
